import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CompleteGoogleSignInAction, getUserInfoFromToken } from "../../store/action";
import { ACCESS_TOKEN } from "../../constants/commonConst";
import { spinnerClass } from "../dashboard/dashboardStyles";
import { usePageMeta } from "../../hooks/usePageMeta";
import { notify } from "../../utils/notify";

/**
 * `/auth/google` — where the API's Google callback sends the browser, with
 * `#code=…&remember=…&created=…` in the fragment.
 *
 * Trades the one-minute code for a session exactly as the login form would,
 * then goes where a login goes. The code leaves the address bar before the
 * request: spent or not, it does not belong in history. A missing, spent or
 * late code sends the person back to the login page to try again.
 *
 * Outside PublicRoute in App.jsx, like /activate: a session already in this
 * browser must not bounce the person away mid-sign-in.
 */
const GoogleSignInComplete = ({ onLoginSuccess }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  // A code is good once. StrictMode runs effects twice in development, and the
  // second exchange would fail and undo the first one's redirect.
  const started = useRef(false);
  usePageMeta({ title: `${t("GoogleSignIn.completing")} | EasyPlanner` });

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const params = new URLSearchParams(location.hash.replace(/^#/, ""));
    const code = params.get("code");
    window.history.replaceState(window.history.state, "", location.pathname);

    const backToLogin = (reason) =>
      navigate({ pathname: "/login", hash: `#google_error=${reason}` }, { replace: true });

    if (!code) {
      backToLogin("expired");
      return;
    }

    CompleteGoogleSignInAction(code, params.get("remember") === "1")
      .then((data) => {
        localStorage.setItem(ACCESS_TOKEN, data.accessToken);
        const email = getUserInfoFromToken()?.email;
        if (email) localStorage.setItem("USER_EMAIL", email);
        onLoginSuccess?.();
        // No CompleteRegistration for Meta here, on purpose. This is a fresh
        // page load, and only CookieConsent loads the pixel, on our public
        // pages — so it is not running. Loading it now would also queue the
        // event until fbevents.js arrives, by which time the address is the
        // dashboard's, and that is the URL Meta would get.
        if (params.get("created") === "1") {
          notify.success(t("GoogleSignIn.created"));
        }
        navigate("/dashboard/organization", { replace: true });
      })
      .catch((error) => {
        // 400 is the API saying the code is unknown, spent or late; anything
        // else is the API not answering, which is worth saying differently.
        backToLogin(error?.response?.status === 400 ? "expired" : "failed");
      });
  }, []);

  return (
    <div className="google-signin-complete flex min-h-screen items-center justify-center bg-ink-50 px-4">
      <div className="text-center">
        <div role="status" aria-live="polite" className="flex flex-col items-center gap-4">
          <span className={spinnerClass} aria-hidden="true" />
          <span className="text-fluid-sm text-ink-600">{t("GoogleSignIn.completing")}</span>
        </div>
      </div>
    </div>
  );
};

GoogleSignInComplete.propTypes = {
  onLoginSuccess: PropTypes.func,
};

export default GoogleSignInComplete;
